import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "core/hooks/useTranslation";
import useDevice from "core/hooks/useDevice";

import Spin from "core/ui-kit/spin";
import Modal from "core/ui-kit/modal";
import Responsive from "core/ui-kit/responsive";
import Confirmation from "core/ui-kit/confirmation";

import Details from "../details";
import Navigation from "../navigation";
import Empty from "../empty";
import CommissionsPopupEdit from "../commissionsEditPopup";

import commissionService from "core/services/apiServices/commissionService";

import { COMMISSION_POPUP_MODE } from "../constants";

import showSuccess from "core/helpers/common/showSuccess";

import { POPUP_SIZE } from "core/constants/common";


const CommissionsView = ({
     affiliateId,
     affiliateLongId,
     editable,
     triggerKey,
     affiliateName,
}) => {
    const { t } = useTranslation();
    const { isMobile } = useDevice();

    const [isLoading, setIsLoading] = useState(false);
    const [commissions, setCommissions] = useState([]);

    const [selectedCommission, setSelectedCommission] = useState(null);

    const [isEditCommissionModalOpened, setIsEditCommissionModalOpened] = useState(false);

    const _commissionsPopupEditRef = useRef(null);

    const [mobileDetailsModalOpened, setMobileDetailsModalOpened] = useState(false);

    const [editingDisabled, setEditingDisabled] = useState(false);

    const [formIsChanged, setFormIsChanged] = useState(false);

    const onCloseMobileModal = () => {
        setMobileDetailsModalOpened(false);
    }

    const [confirmationModal, setConfirmationModal] = useState({
        isVisible: false,
        onOk: Function.prototype
    });

    const removeCommission = useCallback(() => {
        setIsLoading(true);
        commissionService.removeCommission({
            id: selectedCommission.id,
            affiliateCommissionPlanId: selectedCommission.affiliateCommissionPlanId,
            affiliateId,
        })
            .then(({message}) => {
                showSuccess(message);
                const updated = commissions.filter(c => c.affiliateCommissionPlanId !== selectedCommission.affiliateCommissionPlanId)
                setCommissions(updated);
                if(updated.length > 0){
                    setSelectedCommission(updated[0])
                }
                if (isMobile) {
                    setMobileDetailsModalOpened(false);
                }
            })
            .finally(() => {
                setIsLoading(false);
            })
    }, [selectedCommission, affiliateId, setCommissions, commissions, setIsLoading])

    const handleDeleteButtonClick = useCallback(() => {
        setConfirmationModal({
            isVisible: true,
            title: t('backoffice.common.remove'),
            message: t("backoffice.commissionplans.removeCommissionPlanMessage")
                .replace("%%COMMISSION_NAME%%", selectedCommission.name)
                .replace("%%AFFILIATE_USERNAME%%", affiliateName),
            onOk: removeCommission
        })
    }, [setConfirmationModal, removeCommission])

    const hideConfirmationModal = () => {
        setConfirmationModal({
            isVisible: false,
            onOk: Function.prototype
        })
    }

    const getAffiliateCommissions = (isFetchAfterEdit) => {
        commissionService.getAffiliateCommissions({ affiliateId: affiliateLongId })
            .then(({ data }) => {
                setCommissions(data);
                if(data.length > 0){
                    if (isFetchAfterEdit) {
                        const selectedCommissionId = selectedCommission.affiliateCommissionPlanId;
                        const updatedSelected = (data || []).find((item) => item.affiliateCommissionPlanId === selectedCommissionId);
                        setSelectedCommission(updatedSelected);
                    } else {
                        setSelectedCommission(data[0])
                    }
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const onCloseEditModal = () => {
        setIsEditCommissionModalOpened(false);
    }

    const onOpenEditModal = () => {
        setIsEditCommissionModalOpened(true);
    }

    const onSaveButtonClick = () => {
        _commissionsPopupEditRef.current.onSaveButtonClick();
    }

    const onCommissionEdited = () => {
        onCloseEditModal();
        setFormIsChanged(false);
        getAffiliateCommissions(true);
    }

    useEffect(() => {
        getAffiliateCommissions();
    }, [triggerKey]);

    return (
        <div className="rt--commissions-view-content">
            {
                isLoading ? (
                    <div className="rt--loading-wrapper">
                        <Spin />
                    </div>
                ) : (
                    <Fragment>
                        {
                            commissions.length > 0 ? (
                                <Fragment>
                                    <Navigation
                                        items={commissions}
                                        selected={selectedCommission}
                                        onClick={(selectedCommission) => {
                                            setSelectedCommission(selectedCommission);
                                            setMobileDetailsModalOpened(true);
                                        }}
                                    />
                                    <Responsive
                                        mobile={
                                            <Modal
                                                open={mobileDetailsModalOpened}
                                                onCancel={onCloseMobileModal}
                                                title={selectedCommission?.name}
                                            >
                                                <Details
                                                    commission={selectedCommission}
                                                    mode={COMMISSION_POPUP_MODE.VIEW}
                                                    editable={editable}
                                                    onDeleteButtonClick={handleDeleteButtonClick}
                                                    onOpenEditModal={onOpenEditModal}
                                                />
                                            </Modal>
                                        }
                                        desktop={
                                            <Details
                                                commission={selectedCommission}
                                                mode={COMMISSION_POPUP_MODE.VIEW}
                                                editable={editable}
                                                onDeleteButtonClick={handleDeleteButtonClick}
                                                onOpenEditModal={onOpenEditModal}
                                            />
                                        }
                                    />
                                </Fragment>
                            ) : (
                                <Empty mode={COMMISSION_POPUP_MODE.VIEW}/>
                            )
                        }
                    </Fragment>
                )
            }
            {
                confirmationModal.isVisible && (
                    <Confirmation
                        title={confirmationModal?.title ?? ""}
                        message={confirmationModal.message ?? ""}
                        onOk={() => confirmationModal.onOk()}
                        onCancel={hideConfirmationModal}
                        isVisible={confirmationModal.isVisible}
                    />
                )
            }
            <Modal
                open={isEditCommissionModalOpened}
                title={t('backoffice.commissionplans.editCommission')}
                onCancel={onCloseEditModal}
                cancelText={t("backoffice.common.cancel")}
                okText={t("backoffice.commissionplans.save")}
                onOk={onSaveButtonClick}
                size={POPUP_SIZE.BIG}
                okButtonProps={{disabled: editingDisabled || !formIsChanged}}
                centered={false}
            >
                <CommissionsPopupEdit
                    affiliateId={affiliateId}
                    affiliateLongId={affiliateLongId}
                    onCommissionEdited={onCommissionEdited}
                    ref={_commissionsPopupEditRef}
                    selectedCommission={selectedCommission}
                    setSelectedCommission={setSelectedCommission}
                    editingDisabled={editingDisabled}
                    setEditingDisabled={setEditingDisabled}
                    setFormIsChanged={setFormIsChanged}
                />
            </Modal>
        </div>
    )
}

export default CommissionsView;